import React, {MouseEventHandler} from 'react';
import styled, {css} from 'styled-components';
import {
  CampaignBannerTitle,
  Container as CampaignBannerTitleContainer,
} from '../../molecules/CampaignBanner/CampaignBannerTitle';
import {InfoCircleWithDialog} from '../InfoCircle/InfoCircleWithDialog';
import {COLORS} from '../../../constants/colors';
import {
  mobileOnly,
  tabletAndUp,
  desktopAndUp,
} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {FONT_WEIGHTS} from '../../../constants/typography';
import {Icon} from '../../atoms/Icon/Icon';
import {Banner} from '../../atoms/Banner/Banner';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {CountdownBoxed} from '../../molecules/Countdown/CountdownBoxed';
import {RowSection} from '../../molecules/SplitView/RowSection';

export interface DiscountBannerProps {
  link?: string;
  conditionText?: string;
  title: string;
  subTitle?: string;
  endDate?: Date;
  backgroundColor?: string;
  reducedSize?: boolean;
  asSimpleBanner?: boolean;
  onInfoClick?: MouseEventHandler;
  onClick?: () => void;
}

const TimeBox = ({
  endDate,
  reducedSize,
}: {
  endDate?: Date;
  reducedSize?: boolean;
}) => {
  if (!endDate || reducedSize) {
    return null;
  }

  return (
    <TimeBoxWrapper data-testid="TimeBoxWrapper">
      <CountdownBoxed
        size="small"
        endDate={endDate}
        backgroundColor="rgba(0,0,0,0.1)"
        light
      />
    </TimeBoxWrapper>
  );
};

const CampaignBannerContent = (props: DiscountBannerProps) => {
  const {onClick, link, title, subTitle, reducedSize, asSimpleBanner, endDate} =
    props;

  return (
    <CampaignBannerContentWrapper
      $reversedOrder={asSimpleBanner}
      onClick={onClick}
    >
      {endDate && <TimeBox reducedSize={reducedSize} endDate={endDate} />}
      <StyledCampaignBannerTitle
        $leftAligned={asSimpleBanner}
        $small={title.length > 50 && !!endDate}
        title={title}
        bannerLink={link}
        reduced
      />
      {subTitle && <SubTitle>{subTitle}</SubTitle>}
    </CampaignBannerContentWrapper>
  );
};

export const DiscountBanner = (props: DiscountBannerProps) => {
  const {onClick, link, conditionText, asSimpleBanner, onInfoClick} = props;

  return (
    <StyledBanner
      $reducedSize={props.reducedSize}
      backgroundColor={props?.backgroundColor}
      dataTestid={asSimpleBanner ? 'SimpleBanner' : 'CampaignBannerpersonal'}
      small
      bannerLink={link}
      onBannerClick={onClick}
      $backgroundColor={props?.backgroundColor}
    >
      {!asSimpleBanner && <SaleIcon asset={GenericIconName.SaleCloud} />}

      <RowSection constrained={true}>
        <Wrapper>
          <CampaignBannerContent {...props} />
          {conditionText && (
            <StyledInfoCircleWithDialog
              conditions={conditionText}
              iconSize={'SS'}
            />
          )}
          {onInfoClick && (
            <div data-info-wrapper onClick={onInfoClick}>
              <Icon asset={GenericIconName.InfoFilled} size="XS" />
            </div>
          )}
        </Wrapper>
      </RowSection>
    </StyledBanner>
  );
};

const SubTitle = styled.div`
  color: ${COLORS.White};
  font-size: 10px;
  font-weight: bold;
  margin-left: 8px;
`;

const StyledCampaignBannerTitle = styled(CampaignBannerTitle)<{
  $leftAligned?: boolean;
  $small?: boolean;
}>`
  font-size: 12px;
  font-weight: bold;
  margin-left: 10px;

  ${({$leftAligned}) =>
    $leftAligned &&
    mobileOnly(css`
      text-align: left;
      line-height: 1rem;
      font-weight: ${FONT_WEIGHTS.light};
      margin-left: 0px;
    `)}

  ${({$small}) =>
    $small &&
    mobileOnly(css`
      font-size: 10px;
    `)}

  ${css`
    ${tabletAndUp(css`
      font-size: 14px;
    `)}

    ${desktopAndUp(css`
      font-size: 16px;
    `)}
  `}
`;

const StyledInfoCircleWithDialog = styled(InfoCircleWithDialog)`
  position: relative;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  margin-left: 8px;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  width: 100%;
  justify-content: space-between;
`;

const StyledBanner = styled(Banner)<{
  $backgroundColor?: string;
  $reducedSize?: boolean;
}>`
  z-index: ${Z_INDEXES.DISCOUNT_BANNER};
  height: ${({$reducedSize}) => ($reducedSize ? '36px' : '50px')};
  padding-top: 0;
  padding-bottom: 0;
  ${props =>
    !props.$backgroundColor &&
    `background-image: linear-gradient(59deg, #e72d34 -5%, #ffa31a 93%);`}
`;

const SaleIcon = styled(Icon)`
  position: absolute;
  top: -50px;
  left: -45px;
  width: 100px;
  height: 100px;
  opacity: 0.15;
`;

const CampaignBannerContentWrapper = styled.div<{$reversedOrder?: boolean}>`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;

  ${({$reversedOrder}) =>
    $reversedOrder &&
    mobileOnly(css`
      justify-content: space-between;
      flex-direction: row-reverse;

      > ${CampaignBannerTitleContainer} {
        flex: 1;
      }
    `)}
`;

const TimeBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
`;
